import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { MutationConfig } from 'lib/react-query';
import { LivretEleve, LivretEval } from '../types/livret';
import livretKeys from './keys';
import { useTranslation } from 'react-i18next';

interface GenerateLivretDTO {
  livretEvalId: string;
  eleveIds: string[];
}

function generateLivretEleves({
  schoolyearId,
  schoolId,
  eleveIds,
  livretEvalId,
}: {
  schoolyearId: string;
  schoolId: string;
  eleveIds: string[];
  livretEvalId: string;
}): Promise<LivretEleve[]> {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets/${livretEvalId}/livret-eleves`;
  return request(requestURL, {
    method: 'POST',
    body: {
      eleveIds,
    },
  }).then(response => response.data);
}

interface Config {
  config?: MutationConfig<(p: GenerateLivretDTO) => Promise<any>>;
}

export function useGenerateLivretEleves({ config }: Config = {}) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['carnet']);
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation<
    LivretEleve[],
    any,
    GenerateLivretDTO
  >({
    mutationFn: (payload: GenerateLivretDTO) =>
      generateLivretEleves({
        schoolId: school.id,
        schoolyearId: schoolyear.id,
        ...payload,
      }),
    onSuccess: (res: LivretEleve[], { livretEvalId }) => {
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (l: LivretEval) => {
          if (!l) {
            return l;
          }
          return {
            ...l,
            livretsEleves: [...l.livretsEleves, ...res],
          };
        },
      );
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: livretKeys.livretList(schoolyear.id, school.id),
      });
      notifyError({
        title: t('carnet:messages.livret.createError'),
      });
    },
    ...config,
  });
  return { mutateAsync, isLoading };
}
